// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential.

/**
 * Loading an old estimate in as a version of this project.
 *
 * The thing stopping an estimator moving off a spreadsheet is not that the new
 * software is hard; it is that their record of the last three years lives in
 * the old one. A project whose history starts today is a project with no
 * history. This lets them bring the real versions in — the GMP, the 50% CD,
 * whatever they actually sent — so the comparison, the notes and the owner
 * summary have something to work on from the first day.
 *
 * It shows the total before it saves anything. That is the whole trust
 * exercise: an estimator who sees the number they remember sending will
 * believe what the software says afterwards, and one who sees a number three
 * thousand dollars adrift will re-check every line by hand, which is the
 * moment they stop using it.
 */

import React, { useRef, useState } from "react";
import type { Theme } from "../lib/theme";
import {
  importEstimateFile, warningSummary, estimateSubtotal,
  type ImportWarnings,
} from "../lib/estimateImport";
import {
  fetchOrgCostCodes, catalogForPrompt, validCodeSet, unmappedSubcategories,
} from "../lib/orgCostCodes";
import type { ShowToast } from "../types";

const money = (n: number) => "$" + Math.round(n).toLocaleString();

type Stage = "pick" | "working" | "review";

export default function ImportVersionDialog({
  T, FONT, orgId, onClose, onImport, showToast,
}: {
  T: Theme;
  FONT: string;
  /** Whose cost codes the import should be mapped onto. */
  orgId?: string | null;
  onClose: () => void;
  /** Hands back the parsed state plus the label and date to file it under. */
  onImport: (state: any, label: string, dateISO: string) => void;
  showToast: ShowToast;
}) {
  const [stage, setStage] = useState<Stage>("pick");
  const [file, setFile] = useState<File | null>(null);
  const [label, setLabel] = useState("");
  const [dateISO, setDateISO] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [state, setState] = useState<any>(null);
  const [warnings, setWarnings] = useState<ImportWarnings | null>(null);
  const [unmapped, setUnmapped] = useState<Array<{ name: string; costCode: string; lines: number }>>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  const pick = (f: File | null) => {
    if (!f) return;
    setFile(f);
    setError(null);
    // A sensible first guess at the label, which they will usually rewrite.
    if (!label) setLabel(f.name.replace(/\.[^.]+$/, ""));
  };

  const run = async () => {
    if (!file) return;
    setStage("working");
    setError(null);
    try {
      const { supabase } = await import("../lib/supabase");
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error("Please sign in to import an estimate.");
      // The firm's own codes go with the file, so the result lands in their
      // taxonomy rather than the built-in one.
      const catalog = await fetchOrgCostCodes(orgId);
      const out = await importEstimateFile(
        file, session.access_token, setProgress, catalogForPrompt(catalog.all),
      );
      setState(out.projectState);
      setWarnings(out.warnings);
      setUnmapped(unmappedSubcategories(out.projectState, validCodeSet(catalog.all)));
      setStage("review");
    } catch (e: any) {
      // Not everything thrown is an Error. A PDF worker that fails to load
      // rejects with something that has no message, and reporting that as a
      // bare "Import failed" is how a diagnosable problem becomes a mystery.
      const detail =
        (typeof e === "string" && e) ||
        e?.message ||
        e?.error?.message ||
        e?.name ||
        (() => { try { return JSON.stringify(e).slice(0, 160); } catch { return ""; } })();
      setError(detail ? String(detail) : "Import failed for an unknown reason. The browser console has the details.");
      if (e) console.error("[import] failed:", e);
      setStage("pick");
    }
  };

  const save = () => {
    if (!state) return;
    const when = dateISO
      ? new Date(dateISO + "T12:00:00").toISOString()
      : new Date().toISOString();
    onImport(state, label.trim() || file?.name || "Imported version", when);
    showToast(`Imported "${label.trim() || "version"}" into history`);
    onClose();
  };

  const subtotal = state ? estimateSubtotal(state) : 0;
  const warnText = warningSummary(warnings);
  const divCount = state?.divisions?.filter((d: any) =>
    d.subcategories?.some((s: any) => s.rows?.length)).length ?? 0;
  const lineCount = state ? Object.keys(state.itemState || {}).length : 0;

  const field: React.CSSProperties = {
    width: "100%", background: T.bgInput, color: T.textPrimary,
    border: `1px solid ${T.borderSecondary}`, borderRadius: 3,
    padding: "7px 10px", fontSize: 12, fontFamily: FONT, outline: "none",
  };
  const label10: React.CSSProperties = {
    fontSize: 10, letterSpacing: ".1em", textTransform: "uppercase",
    color: T.textWeak, fontWeight: 700, display: "block", marginBottom: 4,
  };

  return (
    <div onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 300, background: T.bgModalBackdrop,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: T.bgModal, border: `1px solid ${T.borderSecondary}`, borderRadius: 10,
          width: "min(560px, 100%)", fontFamily: FONT, overflow: "hidden" }}>

        <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.borderPrimary}` }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.textPrimary }}>Import a past version</div>
          <div style={{ fontSize: 11, color: T.textWeak, marginTop: 3, lineHeight: 1.5 }}>
            Load an estimate you already have — Excel, CSV or PDF — in as a version of this project,
            so your history starts where the work actually did.
          </div>
        </div>

        <div style={{ padding: "18px 20px", display: "flex", flexDirection: "column", gap: 14 }}>
          {stage === "working" ? (
            <div style={{ padding: "26px 0", textAlign: "center" }}>
              <div style={{ fontSize: 13, color: T.textSecondary, marginBottom: 6 }}>Reading {file?.name}…</div>
              <div style={{ fontSize: 11, color: T.textWeak }}>
                {progress > 0 ? `${progress.toLocaleString()} characters so far` : "Sending to the importer"}
              </div>
            </div>
          ) : stage === "review" ? (
            <>
              {/* The number they will check against their own file. */}
              <div style={{ border: `1px solid ${T.borderAccent}55`, background: `${T.borderAccent}0f`,
                borderRadius: 6, padding: "14px 16px" }}>
                <span style={label10}>Imported direct cost</span>
                <div style={{ fontSize: 24, fontWeight: 700, color: T.textPrimary, fontVariantNumeric: "tabular-nums" }}>
                  {money(subtotal)}
                </div>
                <div style={{ fontSize: 11, color: T.textSecondary, marginTop: 8, lineHeight: 1.55 }}>
                  Check this against the file you imported before saving. If it does not match, the
                  version is worth re-importing rather than correcting line by line.
                </div>
              </div>

              <div style={{ fontSize: 11, color: T.textMuted }}>
                {lineCount.toLocaleString()} line {lineCount === 1 ? "item" : "items"} across {divCount} division{divCount === 1 ? "" : "s"}.
              </div>

              {unmapped.length > 0 && (
                <div style={{ fontSize: 11, color: T.textSecondary, background: `${T.borderAccent}0f`,
                  border: `1px solid ${T.borderAccent}44`, borderRadius: 6, padding: "9px 12px", lineHeight: 1.55 }}>
                  <strong style={{ color: T.textPrimary }}>
                    {unmapped.reduce((n, u) => n + u.lines, 0)} line{unmapped.reduce((n, u) => n + u.lines, 0) === 1 ? "" : "s"} did not match one of your cost codes
                  </strong>{" "}
                  and {unmapped.length === 1 ? "is" : "are"} filed as unassigned: {unmapped.slice(0, 4).map(u => u.name).join(", ")}
                  {unmapped.length > 4 ? `, +${unmapped.length - 4} more` : ""}. Label them in the Estimate tab when you have a minute —
                  they count toward the total either way.
                </div>
              )}

              {warnText && (
                <div style={{ fontSize: 11, color: "#f6ad55", background: "#f6ad5514",
                  border: "1px solid #f6ad5544", borderRadius: 6, padding: "8px 12px", lineHeight: 1.5 }}>
                  The importer cleaned up: {warnText}. Those lines are marked “Needs Review”.
                </div>
              )}

              <div>
                <span style={label10}>Version name</span>
                <input value={label} onChange={(e) => setLabel(e.target.value)}
                  placeholder="GMP, 50% CD, Owner Budget…" style={field} />
              </div>
              <div>
                <span style={label10}>Date of this version</span>
                <input type="date" value={dateISO} onChange={(e) => setDateISO(e.target.value)} style={field} />
                <div style={{ fontSize: 10, color: T.textWeak, marginTop: 4 }}>
                  The date it was issued, not today — this is what orders the history.
                </div>
              </div>
            </>
          ) : (
            <>
              <button onClick={() => fileRef.current?.click()}
                style={{ border: `1px dashed ${file ? T.borderAccent : T.borderSecondary}`, borderRadius: 6,
                  background: file ? `${T.borderAccent}0f` : "transparent", color: file ? T.textPrimary : T.textMuted,
                  padding: "22px 16px", fontSize: 12, fontFamily: FONT, cursor: "pointer", width: "100%" }}>
                {file ? file.name : "Choose an estimate file (.xlsx, .csv, .pdf)"}
              </button>
              <input ref={fileRef} type="file" accept=".xlsx,.xls,.xlsm,.csv,.pdf"
                onChange={(e) => { pick(e.target.files?.[0] || null); e.currentTarget.value = ""; }}
                style={{ display: "none" }} />
              {error && (
                <div style={{ fontSize: 11, color: T.negative, background: `${T.negative}14`,
                  border: `1px solid ${T.negative}44`, borderRadius: 6, padding: "8px 12px" }}>
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        <div style={{ padding: "12px 20px", borderTop: `1px solid ${T.borderPrimary}`,
          display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} disabled={stage === "working"}
            style={{ background: "none", border: `1px solid ${T.borderSecondary}`, color: T.textMuted,
              borderRadius: 3, padding: "7px 14px", fontSize: 12, fontFamily: FONT,
              cursor: stage === "working" ? "default" : "pointer", opacity: stage === "working" ? 0.5 : 1 }}>
            Cancel
          </button>
          {stage === "review" ? (
            <button onClick={save}
              style={{ background: T.borderAccent, border: "none", color: T.textOnGold, fontWeight: 700,
                borderRadius: 3, padding: "7px 16px", fontSize: 12, fontFamily: FONT, cursor: "pointer" }}>
              Save as version
            </button>
          ) : (
            <button onClick={run} disabled={!file || stage === "working"}
              style={{ background: file ? T.borderAccent : T.borderSecondary, border: "none",
                color: file ? T.textOnGold : T.textWeak, fontWeight: 700, borderRadius: 3,
                padding: "7px 16px", fontSize: 12, fontFamily: FONT,
                cursor: file && stage !== "working" ? "pointer" : "default" }}>
              {stage === "working" ? "Reading…" : "Read estimate"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
